// React Hook for Shadowing练习统计
import { useState, useEffect, useCallback, useRef } from 'react';
import { shadowingTracker, DashboardStats, ShadowingRecord } from '@/lib/shadowing-tracker';

export function useShadowingStats(userId?: string) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUserId, setLastUserId] = useState<string | undefined>(userId);

  const loadStats = useCallback(async () => {
    try {
      // 当没有 userId（仍在加载或未登录）时，跳过请求
      if (!userId) {
        setStats(null);
        setError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);

      const dashboardStats = await shadowingTracker.getDashboardStats(userId);
      setStats(dashboardStats);
    } catch (err) {
      console.error('Failed to load shadowing stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    // 如果userId变化了，重置状态并重新加载
    if (lastUserId !== userId) {
      setStats(null);
      setError(null);
      setLoading(true);
      setLastUserId(userId);
    }

    loadStats();

    // 监听相关事件
    const handleRecordSaved = () => {
      // 短暂延迟后刷新，确保Redis数据已更新
      setTimeout(loadStats, 500);
    };

    const handleGoalUpdated = () => {
      loadStats();
    };

    const handleGoalCompleted = () => {
      // 显示庆祝效果
      console.log('🎉 Daily goal completed!');
      loadStats();
    };

    window.addEventListener('shadowingRecordSaved', handleRecordSaved);
    window.addEventListener('shadowingGoalUpdated', handleGoalUpdated);
    window.addEventListener('shadowingGoalCompleted', handleGoalCompleted);

    // 移除定时刷新，只在页面刷新时更新数据

    return () => {
      window.removeEventListener('shadowingRecordSaved', handleRecordSaved);
      window.removeEventListener('shadowingGoalUpdated', handleGoalUpdated);
      window.removeEventListener('shadowingGoalCompleted', handleGoalCompleted);
    };
  }, [loadStats]);

  return {
    stats,
    loading,
    error,
    refresh: loadStats
  };
}

// Hook for records
export function useShadowingRecords(
  userId?: string,
  limit: number = 50,
  options?: { enabled?: boolean }
) {
  const [records, setRecords] = useState<ShadowingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const enabled = options?.enabled ?? true;

  const loadRecords = useCallback(async (reset: boolean = false) => {
    try {
      if (!enabled || !userId) {
        setRecords([]);
        setHasMore(false);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);

      const currentOffset = reset ? 0 : offsetRef.current;
      const newRecords = await shadowingTracker.getRecords(userId, limit, currentOffset);

      if (reset) {
        setRecords(newRecords);
        offsetRef.current = limit;
      } else {
        setRecords(prev => [...prev, ...newRecords]);
        offsetRef.current += limit;
      }

      setHasMore(newRecords.length === limit);
    } catch (err) {
      console.error('Failed to load shadowing records:', err);
      setError(err instanceof Error ? err.message : 'Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [userId, limit, enabled]);

  const loadMore = useCallback(() => {
    if (!enabled) return;
    if (!loading && hasMore) {
      loadRecords(false);
    }
  }, [enabled, loading, hasMore, loadRecords]);

  const refresh = useCallback(() => {
    if (!enabled) return;
    offsetRef.current = 0;
    loadRecords(true);
  }, [enabled, loadRecords]);

  useEffect(() => {
    if (!enabled) return;
    refresh();

    // 监听新记录保存
    const handleRecordSaved = () => {
      setTimeout(refresh, 500);
    };

    window.addEventListener('shadowingRecordSaved', handleRecordSaved);

    return () => {
      window.removeEventListener('shadowingRecordSaved', handleRecordSaved);
    };
  }, [enabled, refresh]);

  // 当 enabled 从 false -> true 时，触发一次加载
  useEffect(() => {
    if (enabled) {
      offsetRef.current = 0;
      setHasMore(true);
    }
  }, [enabled]);

  return {
    records,
    loading,
    error,
    hasMore,
    loadMore,
    refresh
  };
}

// Hook for progress report
export function useShadowingProgress(userId?: string, days: number = 7) {
  const [progressData, setProgressData] = useState<{
    dailyData: { date: string; sessions: number; accuracy: number }[];
    improvement: number;
    mostPracticedSentences: { text: string; count: number }[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProgress = useCallback(async () => {
    try {
      if (!userId) {
        setProgressData(null);
        setLoading(false);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      const data = await shadowingTracker.getProgressReport(userId, days);
      setProgressData(data);
    } catch (err) {
      console.error('Failed to load progress data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  }, [userId, days]);

  useEffect(() => {
    loadProgress();

    // 监听新记录保存
    const handleRecordSaved = () => {
      setTimeout(loadProgress, 500);
    };

    window.addEventListener('shadowingRecordSaved', handleRecordSaved);

    return () => {
      window.removeEventListener('shadowingRecordSaved', handleRecordSaved);
    };
  }, [loadProgress]);

  return {
    progressData,
    loading,
    error,
    refresh: loadProgress
  };
}

// Hook for monthly statistics
export function useShadowingMonthlyStats(userId?: string, months: number = 6) {
  const [monthlyData, setMonthlyData] = useState<{
    month: string;
    value: number;
    sessions: number;
    change: number;
  }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMonthlyStats = useCallback(async () => {
    try {
      if (!userId) {
        setMonthlyData([]);
        setLoading(false);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);

      const data = await shadowingTracker.getMonthlyStats(userId, months);
      setMonthlyData(data);
    } catch (err) {
      console.error('Failed to load monthly stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to load monthly stats');
    } finally {
      setLoading(false);
    }
  }, [userId, months]);

  useEffect(() => {
    loadMonthlyStats();

    // 监听新记录保存
    const handleRecordSaved = () => {
      setTimeout(loadMonthlyStats, 500);
    };

    window.addEventListener('shadowingRecordSaved', handleRecordSaved);

    return () => {
      window.removeEventListener('shadowingRecordSaved', handleRecordSaved);
    };
  }, [loadMonthlyStats]);

  return {
    monthlyData,
    loading,
    error,
    refresh: loadMonthlyStats
  };
}

// Hook for saving records
export function useSaveShadowingRecord() {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveRecord = useCallback(async (data: {
    userId?: string;
    episodeId: string;
    episodeTitle: string;
    episodeThumbnail?: string;
    sentenceText: string;
    userTranscription: string;
    accuracy: number;
    duration: number;
    quizSequence?: number;
  }) => {
    try {
      setSaving(true);
      setError(null);
      await shadowingTracker.saveRecord(data);
      return true;
    } catch (err) {
      console.error('Failed to save shadowing record:', err);
      setError(err instanceof Error ? err.message : 'Failed to save record');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    saveRecord,
    saving,
    error
  };
}

// Hook for daily goal management
export function useDailyGoal(userId?: string) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setGoal = useCallback(async (targetSessions: number, targetMinutes: number) => {
    try {
      setSaving(true);
      setError(null);
      await shadowingTracker.setDailyGoal(targetSessions, targetMinutes, userId);
      return true;
    } catch (err) {
      console.error('Failed to set daily goal:', err);
      setError(err instanceof Error ? err.message : 'Failed to set goal');
      return false;
    } finally {
      setSaving(false);
    }
  }, [userId]);

  return {
    setGoal,
    saving,
    error
  };
}
